import Paper from '@mui/material/Paper';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../reducers/store';
import { User } from '../model/user';
import { Group } from '../model/group';
import { useEffect, useState } from 'react';
import { Box, Button, Checkbox, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { UserRowView } from './userrow';
import { UserInputs } from './userinputs';
import { Loader } from './loader';
import { AlertView } from './alert';
import { AxiosError } from 'axios';
import { getApiAccessToken } from '../auth/auth';
import { useMsal } from '@azure/msal-react';
import { getGroupUsersAsync } from '../reducers/userSlice';
import { Program, ProgramUser } from '../model/program';
import { ProgramUserRowView } from './programuserrow';
import { addUsersToProgramAsync } from '../reducers/userSlice';
import AddIcon from '@mui/icons-material/Add';
import { getUserRole, UserRole } from '../auth/jwt';

export const UserView = () => {
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();
    const dispatch: AppDispatch = useDispatch();
    
    const currentProgram: Program | null = useSelector((state: RootState) => state.programs.currentProgram);
    const users: User[] = useSelector((state: RootState) => state.users.users);
    const currentProgramUsers: ProgramUser[] | null = useSelector((state: RootState) => state.users.currentProgramUsers);
    const groupUsers: User[] = useSelector((state: RootState) => state.users.currentGroupUsers);
    const currentGroup: Group | null = useSelector((state: RootState) => state.groups.currentGroup);
    const currentClientAdmins: User[] | null = useSelector((state: RootState) => state.users.currentClientAdmins);
    
    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setError] = useState<string | null>(null);
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);


    const reloadUsers = async (group: Group | null) => {
        try {
            if (!group) {
                setError("could not find current client for users")
                return 
            }

            setLoading(true);

            let accessToken: string = await getApiAccessToken(instance, activeAccount)
            dispatch(getGroupUsersAsync({accessToken, groupId: group.id}))

            setLoading(false);

        } catch (error) {
            if (error instanceof AxiosError) {
                console.log(error)
                setError(error?.message)
            } else if (error instanceof Error) {
                console.error(error);
            }
            setLoading(false);
        }
    }

    const handleSelectAll = (isChecked: boolean) => {
        if (isChecked) {
            const allUserIds = currentGroup?.displayName === "Public"
                ? users.map(user => user.id)
                : groupUsers?.map(user => user.id);
            setSelectedUsers(allUserIds);
        } else {
            setSelectedUsers([]);
        }
    };

    const handleToggleUserSelection = (userId: string) => {
        setSelectedUsers(prev =>
            prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
        );
    };

    const handleBulkAdd = async () => {
        if (!currentProgram) {
            setError("No program selected");
            return;
        }

        setLoading(true);
        try {
            const accessToken = await getApiAccessToken(instance, activeAccount);
            const selectedUserObjects = groupUsers.filter(user => selectedUsers.includes(user.id));
            await dispatch(addUsersToProgramAsync({ accessToken, users: selectedUserObjects, program: currentProgram }));
            setSelectedUsers([]);
        } catch (error) {
            if (error instanceof AxiosError) {
                setError(error?.message);
            } else if (error instanceof Error) {
                console.error(error);
            }
        }
        setLoading(false);
    };

    // if all users of the client are already part of the program then there is no need to show ability to bulk add
    const allUsersAlreadyAdded = (userArray: User[], programUserArray: ProgramUser[] | null): boolean => {
        if (userArray.length !== programUserArray?.length) {
            return false;
        }

        // Create sets of user IDs
        const set1 = new Set(userArray.map(user => user.id));
        const set2 = new Set(programUserArray.map(user => user.userId));

        // Compare sets
        if (set1.size !== set2.size) {
            return false;
        }

        let equal = Array.from(set1).every((id) => set2.has(id));
        return equal
    };
    
    const showMakeClientAdmin = getUserRole(activeAccount) == UserRole.ADMIN
    
    return (
        <div style={{padding: 16}}>
            <AlertView error={errorMessage}/>
            <UserInputs 
                onAdded={() => {reloadUsers(currentGroup)}} 
                group={currentGroup}
            />

            <Typography sx={{ marginBottom: 1 }} align='left'>{currentProgram?.name} </Typography>
            <Typography sx={{ marginBottom: 2, color: 'gray', fontSize: '0.8em' }} align='left'>{currentProgramUsers?.length ?? 0} users</Typography>

            <TableContainer sx={{ marginBottom: 5 }} component={Paper}>
                <Table aria-label="simple table">
                    <TableHead>
                    <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Remove from Program</TableCell>
                    </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            loading ? <Loader width={20} height={20}/> :
                            currentProgramUsers?.map(user => <ProgramUserRowView user={user} program={currentProgram} />)
                        }
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography sx={{ marginBottom: 1 }} align='left'>All Users</Typography>
            <Typography sx={{ marginBottom: 2, color: 'gray', fontSize: '0.8em' }} align='left'>{currentGroup?.displayName == "Public" ? users.length: groupUsers.length} users</Typography>

            {
                !allUsersAlreadyAdded(groupUsers, currentProgramUsers) && selectedUsers.length > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleBulkAdd}
                        >
                            <AddIcon /> Add Selection to Program
                        </Button>
                    </Box>
                )
            }

            <TableContainer component={Paper}>
                <Table aria-label="simple table">
                    <TableHead>
                    <TableRow>
                        <TableCell>
                        {
                            !allUsersAlreadyAdded(groupUsers, currentProgramUsers) && (
                                <Checkbox
                                    checked={
                                        currentGroup?.displayName === "Public"
                                            ? selectedUsers.length === users.length
                                            : selectedUsers.length === groupUsers.length
                                    }
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                />
                            )
                        }
                        </TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Add to Program</TableCell>
                        <TableCell>
                            Verified
                        </TableCell>
                        {
                            showMakeClientAdmin && 
                            <TableCell>Make Admin</TableCell>
                        }
                        <TableCell>
                            Delete
                        </TableCell>
                    </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            // The actual 'Public' Group in Azure is more of a placeholder to distinguish it from other groups
                            // and doesn't contain any users. Therefore we bypass showing users of the Public program. 
                            // Since everyone has access to public courses we show all users by default
                            loading ? <Loader width={20} height={20}/> :
                                currentGroup?.displayName == "Public" ?
                                users.map(user => (
                                    <UserRowView
                                        key={user.id}
                                        programUsers={currentProgramUsers} 
                                        user={user} 
                                        group={currentGroup} 
                                        program={currentProgram}
                                        selected={selectedUsers.includes(user.id)}
                                        toggleSelection={handleToggleUserSelection}
                                        showMakeClientAdmin={showMakeClientAdmin}
                                        isClientAdmin={currentClientAdmins.map(admin => admin.id).includes(user.id)} />
                                    )
                                ) :
                                groupUsers?.map(user => (
                                    <UserRowView 
                                        key={user.id}
                                        programUsers={currentProgramUsers} 
                                        user={user} 
                                        group={currentGroup} 
                                        program={currentProgram}
                                        selected={selectedUsers.includes(user.id)}
                                        toggleSelection={handleToggleUserSelection}
                                        showMakeClientAdmin={showMakeClientAdmin}
                                        isClientAdmin={currentClientAdmins.map(admin => admin.id).includes(user.id)} />
                                    )
                                )
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
}